import React, { useState, useEffect, useRef, Fragment } from 'react';
import * as apiService from '../../services/apiService';
import { formatDate } from '../../utils/dateUtils';
import { getStaffSizeLabel } from '../../utils/inseeCodeMappings';
import { ClipboardIcon, CheckIcon, MapPinIcon, MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Dialog, Transition } from '@headlessui/react';

interface InseeEstablishmentsCardProps {
  siren: string;
}

interface Establishment {
  siret: string;
  siren: string;
  dateCreationEtablissement?: string;
  activitePrincipaleEtablissement?: string;
  numeroVoieEtablissement?: string;
  typeVoieEtablissement?: string;
  libelleVoieEtablissement?: string;
  codePostalEtablissement?: string;
  libelleCommuneEtablissement?: string;
  etatAdministratifEtablissement?: string;
  etablissementSiege?: string;
  trancheEffectifsEtablissement?: string;
  naf?: {
    intitule?: string;
  };
  uniteLegale?: {
    trancheEffectifsUniteLegale?: string;
    denominationUniteLegale?: string;
    nomUniteLegale?: string;
    prenomUsuelUniteLegale?: string;
  };
  isAdherent?: boolean;
}

interface NDWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: 'map' | 'copy';
}

const NDWarningModal: React.FC<NDWarningModalProps> = ({ isOpen, onClose, onConfirm, actionType }) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Données limitées
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Certaines informations de cette entreprise sont marquées comme non divulguées [ND] dans les données ouvertes.
                        {actionType === 'map' 
                          ? " L'adresse est donc très minimaliste et peut ne pas être précise sur Google Maps." 
                          : " L'adresse copiée peut être incomplète."}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Nous ne pouvons pas fournir plus d'informations que celles déjà affichées.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={() => {
                      onClose();
                      onConfirm();
                    }}
                  >
                    Continuer quand même
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Annuler
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const InseeEstablishmentsCard: React.FC<InseeEstablishmentsCardProps> = ({ siren }) => {
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSiret, setCopiedSiret] = useState<string | null>(null);
  const [showNDWarning, setShowNDWarning] = useState<boolean>(false);
  const [ndWarningType, setNdWarningType] = useState<'map' | 'copy'>('map');
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const prevPage = useRef(pagination.page);
  const userInitiatedPageChange = useRef(false);

  // Fetch establishments data
  useEffect(() => {
    const fetchEstablishments = async () => {
      if (!siren) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const data = await apiService.getEstablishmentsBySiren(siren, pagination.page, pagination.limit);
        setEstablishments(data.establishments);
        setPagination(data.pagination);
      } catch (err) {
        console.error('Error fetching establishments:', err);
        setError('Erreur lors de la récupération des établissements. Veuillez réessayer ultérieurement.');
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
  }, [siren, pagination.page, pagination.limit]);

  // Scroll to top only when page changes through user interaction
  useEffect(() => {
    // Only scroll if the page change was initiated by the user
    if (userInitiatedPageChange.current && componentRef.current) {
      componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      userInitiatedPageChange.current = false;
    }
    
    // Update previous page for next comparison
    prevPage.current = pagination.page;
  }, [pagination.page]);

  // Handle page change - set a flag that user initiated this change
  const handlePageChange = (page: number) => {
    if (page !== pagination.page) {
      userInitiatedPageChange.current = true;
      setPagination((prev) => ({ ...prev, page }));
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      userInitiatedPageChange.current = true;
      setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  };

  // Handle next page
  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      userInitiatedPageChange.current = true;
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedSiret(text);
        setTimeout(() => setCopiedSiret(null), 2000);
      },
      (err) => {
        console.error('Erreur lors de la copie :', err);
      }
    );
  };

  // Format full address
  const formatFullAddress = (establishment: Establishment) => {
    // For display in the UI (keep [ND] values visible)
    const displayComponents = [];
    if (establishment.numeroVoieEtablissement) displayComponents.push(establishment.numeroVoieEtablissement);
    if (establishment.typeVoieEtablissement) displayComponents.push(establishment.typeVoieEtablissement);
    if (establishment.libelleVoieEtablissement) displayComponents.push(establishment.libelleVoieEtablissement);
    
    const displayStreetAddress = displayComponents.join(' ');
    const displayPostalCode = establishment.codePostalEtablissement || '';
    const displayCity = establishment.libelleCommuneEtablissement || '';
    
    // For Google Maps and copy (filter out [ND] values)
    const mapComponents = [];
    if (establishment.numeroVoieEtablissement && establishment.numeroVoieEtablissement !== '[ND]') 
      mapComponents.push(establishment.numeroVoieEtablissement);
    if (establishment.typeVoieEtablissement && establishment.typeVoieEtablissement !== '[ND]') 
      mapComponents.push(establishment.typeVoieEtablissement);
    if (establishment.libelleVoieEtablissement && establishment.libelleVoieEtablissement !== '[ND]') 
      mapComponents.push(establishment.libelleVoieEtablissement);
    
    const mapStreetAddress = mapComponents.join(' ');
    const mapPostalCode = establishment.codePostalEtablissement !== '[ND]' ? establishment.codePostalEtablissement || '' : '';
    const mapCity = establishment.libelleCommuneEtablissement !== '[ND]' ? establishment.libelleCommuneEtablissement || '' : '';
    
    return {
      fullAddress: <>{displayStreetAddress}<br/>{displayPostalCode} {displayCity}</>,
      fullAddressText: `${mapStreetAddress}, ${mapPostalCode} ${mapCity}`
    };
  };

  // Check if address has ND values
  const hasNDValues = (establishment: Establishment) => {
    return [
      establishment.numeroVoieEtablissement,
      establishment.typeVoieEtablissement,
      establishment.libelleVoieEtablissement,
      establishment.codePostalEtablissement,
      establishment.libelleCommuneEtablissement
    ].some(value => value === '[ND]');
  };

  // Handle Google Maps click with warning
  const handleGoogleMapsClick = (e: React.MouseEvent, establishment: Establishment, fullAddressText: string) => {
    e.preventDefault();
    
    if (hasNDValues(establishment)) {
      setNdWarningType('map');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        window.open(getGoogleMapsUrl(fullAddressText), '_blank');
      });
    } else {
      window.open(getGoogleMapsUrl(fullAddressText), '_blank');
    }
  };

  // Handle address copy with warning
  const handleAddressCopy = (establishment: Establishment, fullAddressText: string) => {
    if (hasNDValues(establishment)) {
      setNdWarningType('copy');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        navigator.clipboard.writeText(fullAddressText).then(
          () => {
            setCopiedSiret(`address-${establishment.siret}`);
            setTimeout(() => setCopiedSiret(null), 2000);
          },
          (err) => {
            console.error('Erreur lors de la copie :', err);
          }
        );
      });
    } else {
      navigator.clipboard.writeText(fullAddressText).then(
        () => {
          setCopiedSiret(`address-${establishment.siret}`);
          setTimeout(() => setCopiedSiret(null), 2000);
        },
        (err) => {
          console.error('Erreur lors de la copie :', err);
        }
      );
    }
  };

  // Create Google Maps URL
  const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  // Create Google Search URL
  const getGoogleSearchUrl = (establishment: Establishment) => {
    // Create a search query including company name (from uniteLegale) and SIRET
    const searchQuery = [
      establishment.uniteLegale?.denominationUniteLegale || 
      (establishment.uniteLegale?.nomUniteLegale && establishment.uniteLegale?.prenomUsuelUniteLegale ? 
        `${establishment.uniteLegale.prenomUsuelUniteLegale} ${establishment.uniteLegale.nomUniteLegale}` : 
        establishment.uniteLegale?.nomUniteLegale),
      establishment.codePostalEtablissement,
      establishment.libelleCommuneEtablissement
    ].filter(Boolean).join(' ');
    
    return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
  };

  // Get staff size label
  const getEffectifsLabel = (establishment: Establishment) => {
    const trancheEffectifs = establishment.trancheEffectifsEtablissement || 
      (establishment.uniteLegale ? establishment.uniteLegale.trancheEffectifsUniteLegale : null);
    
    if (!trancheEffectifs) return '-';
    
    return getStaffSizeLabel(trancheEffectifs);
  };

  if (error) {
    return (
      <div ref={componentRef}>
        <p className="text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div ref={componentRef}>
      <div className="flex justify-between items-center mb-4">
        {!loading && establishments.length > 0 && (
          <span className="text-sm text-gray-500">
            {pagination.total} {pagination.total > 1 ? 'établissements' : 'établissement'} trouvé{pagination.total > 1 ? 's' : ''}
          </span>
        )}
      </div>
      
      {loading ? (
        <div className="space-y-4">
          <Skeleton height={24} width="50%" />
          <Skeleton count={5} />
        </div>
      ) : establishments.length === 0 ? (
        <p className="text-sm text-gray-600">Aucun établissement trouvé pour ce SIREN.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    SIRET
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Siège
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Adresse complète
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    État
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Date création
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activité
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Tranche effectifs
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {establishments.map((establishment) => {
                  const { fullAddress, fullAddressText } = formatFullAddress(establishment);
                  return (
                    <tr key={establishment.siret} className="hover:bg-gray-50">
                      <td className="px-3 py-4 text-sm text-gray-900 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="font-medium">{establishment.siret}</span>
                          <button
                            onClick={() => handleCopy(establishment.siret)}
                            className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            title="Copier le SIRET"
                          >
                            {copiedSiret === establishment.siret ? (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardIcon className="h-4 w-4" />
                            )}
                          </button>
                          <a
                            href={getGoogleSearchUrl(establishment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            title="Rechercher sur Google"
                          >
                            <MagnifyingGlassIcon className="h-4 w-4" />
                          </a>
                        </div>
                        {establishment.isAdherent !== undefined && (
                          <div className="mt-1">
                            {establishment.isAdherent ? (
                              <span className="text-xs font-medium text-sky-600">
                                Adhérent
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-gray-400">
                                Non adhérent
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {establishment.etablissementSiege === 'true' ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Oui
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Non
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="mr-2 whitespace-nowrap">{fullAddress || '-'}</span>
                          {fullAddress && (
                            <div className="flex space-x-2">
                              <a 
                                href="#"
                                onClick={(e) => handleGoogleMapsClick(e, establishment, fullAddressText)}
                                className="text-blue-500 hover:text-blue-700"
                                title="Voir sur Google Maps"
                              >
                                <MapPinIcon className="h-4 w-4" />
                              </a>
                              <button
                                onClick={() => handleAddressCopy(establishment, fullAddressText)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Copier l'adresse"
                              >
                                {copiedSiret === `address-${establishment.siret}` ? (
                                  <CheckIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <ClipboardIcon className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap">
                        {establishment.etatAdministratifEtablissement === 'A' ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Actif
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Fermé
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {establishment.dateCreationEtablissement ? formatDate(establishment.dateCreationEtablissement) : '-'}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span>{establishment.activitePrincipaleEtablissement || '-'}</span>
                          {establishment.naf?.intitule && (
                            <span className="text-xs text-gray-400 italic">{establishment.naf.intitule}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {getEffectifsLabel(establishment)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex-1 flex justify-between items-center">
                <button
                  onClick={handlePreviousPage}
                  disabled={pagination.page <= 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    pagination.page <= 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Précédent
                </button>
                <div className="hidden md:flex">
                  {Array.from({ length: Math.min(pagination.pages, 5) }).map((_, i) => {
                    // Calculate which page numbers to show
                    let pageNumber;
                    if (pagination.pages <= 5) {
                      pageNumber = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNumber = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNumber = pagination.pages - 4 + i;
                    } else {
                      pageNumber = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`mx-1 px-4 py-2 border text-sm font-medium rounded-md ${
                          pagination.page === pageNumber
                            ? 'bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                <div className="flex md:hidden">
                  <span className="mx-2 text-sm text-gray-700">
                    Page {pagination.page} sur {pagination.pages}
                  </span>
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={pagination.page >= pagination.pages}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    pagination.page >= pagination.pages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add the warning modal */}
      <NDWarningModal 
        isOpen={showNDWarning} 
        onClose={() => setShowNDWarning(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        actionType={ndWarningType}
      />
    </div>
  );
};

export default InseeEstablishmentsCard; 